import React from 'react';
import { UserPlus, Ticket, Target, CheckCircle2, Trophy } from 'lucide-react';
import FixtureSelection from '../gameDetails/FixtureSelection';
import { GameProgressBoard } from '../gameDetails/GameProgress';
import {
  DEMO_FIXTURES,
  DEMO_GAME_WEEK,
  DEMO_PROGRESS,
  DEMO_TEAM_FORM,
  DEMO_USED_TEAMS,
} from './demoContent';
import './landing.css';

/**
 * What a stranger sees.
 *
 * The page this replaced was three pages stacked on one: a notifications inbox nobody
 * signed out could fill, an app launcher whose three cards all went to the register form
 * with the intent thrown away, and two paragraphs of prose that the Rules page already
 * said better. It answered neither audience.
 *
 * Four decisions hold this one up.
 *
 * **The headline is the mechanic.** "Last Man Standing" is a genre name and means
 * nothing to somebody who has not played one, so it drops to an eyebrow and the first
 * line of the page says what you actually do.
 *
 * **Two product screens, not four.** The pick screen and Game Progress. Form and
 * analytics get a clause of copy each, because four screenshots read as a feature list -
 * which is what every other site in this market already looks like - and the two shown
 * here are the two that carry the argument: this is the thing you do every week, and
 * this is why it beats a group chat and a spreadsheet.
 *
 * **They are the real components.** See `demoContent` - the alternative is images, and
 * images go stale the first time somebody restyles a pick row with nothing failing to
 * say so.
 *
 * **One action, repeated.** Create an account. The code entrance beside it is second
 * rather than absent because most arrivals here were sent a link by a mate rather than
 * going looking, and it says out loud that signing up comes first - a button that
 * silently dumps you on a register form is the thing this page is replacing.
 *
 * **No Gaffer section**, and it is not an oversight. There was one, and it broke the rule
 * `GafferToggle` states for every place he is mentioned: it opened by saying how he picks.
 * That line is his whole appeal spent in one sentence - working him out is half the game -
 * and a stranger has no use for it, because he is a setting inside a game they have not
 * created yet. He is still on this page where he belongs: a row on the Game Progress board
 * above, playing, the way somebody would actually meet him. If he ever needs selling to a
 * signed-out reader, sell what he does to a group, and let the Rules page be the only place
 * that goes near his method - which is to say the only place that refuses to.
 */
function LandingPage({ onNavigate, onAction }) {
  return (
    <div className="lp">

      <section className="lp-hero">
        <div className="lp-hero-copy">
          <span className="lp-eyebrow">Last Man Standing · 2026/27</span>
          <h1 className="lp-title">Pick one team a week.</h1>
          <p className="lp-lede">Win and you&rsquo;re through. Lose or draw and you&rsquo;re out.</p>
          <p className="lp-body">
            And you can only use each team <em>once</em> all season &mdash; so the week you
            spend Arsenal is a decision you live with until May. Last player standing takes it.
          </p>

          <div className="lp-cta">
            <button className="primary-btn" onClick={() => onNavigate('register')}>
              <UserPlus size={20} />
              Create Account
            </button>
            <button className="secondary-btn" onClick={() => onAction('join-game')}>
              <Ticket size={20} />
              I&rsquo;ve got a code
            </button>
          </div>
          <p className="lp-cta-note">
            Sent a code by a mate? Create your account first and it will take you
            straight into their game.
          </p>

          <p className="lp-trust">
            Real fixtures, all season: Premier League · Championship · Serie A · La Liga
          </p>
        </div>

        <figure className="lp-shot">
          <DemoScreen label="The pick screen, as a player sees it">
            <FixtureSelection
              fixtures={DEMO_FIXTURES}
              usedTeams={DEMO_USED_TEAMS}
              teamForm={DEMO_TEAM_FORM}
              gameWeek={DEMO_GAME_WEEK}
            />
          </DemoScreen>
          <figcaption>Every team&rsquo;s last five, right where you choose.</figcaption>
        </figure>
      </section>

      <section className="lp-how">
        <h2 className="lp-sr-only">How it works</h2>
        <ol className="lp-how-list">
          <HowStep icon={Target} title="One pick, every week">
            Choose a team from that week&rsquo;s fixtures before the deadline. Nobody sees
            your pick until the round locks.
          </HowStep>
          <HowStep icon={CheckCircle2} title="Win and you survive">
            A draw is no good to you. And you go out on the day your team lost, not
            whenever somebody gets round to closing the week.
          </HowStep>
          <HowStep icon={Trophy} title="Last one left wins">
            If everybody goes out in the same week the whole thing rolls over and you all
            start again. It happens more than you would think.
          </HowStep>
        </ol>
      </section>

      <section className="lp-progress">
        <div className="lp-progress-copy">
          <span className="lp-eyebrow lp-eyebrow-green">Game progress</span>
          <h2 className="lp-h2">The whole round, in one list.</h2>
          <p className="lp-body">
            Who backed what, who went out on Saturday, and who is quietly running out of
            teams &mdash; read down the season a round at a time. No spreadsheet, no group
            chat, nobody keeping score by hand.
          </p>
          <p className="lp-body lp-body-quiet">
            Picks stay hidden until the deadline passes, and the count of teams somebody
            has left is worked out from the rounds everyone can already see &mdash; so it
            can never give away the pick they have just made.
          </p>
          <p className="lp-body lp-body-quiet">
            There is a form table for every side in the competition, and once the round
            locks, a breakdown of who backed whom.
          </p>
        </div>

        <figure className="lp-shot">
          <DemoScreen label="Game progress, as a player sees it">
            <GameProgressBoard board={DEMO_PROGRESS} />
          </DemoScreen>
        </figure>
      </section>

      <section className="lp-foot">
        <h2 className="lp-h2">Set one up, or join the one you were sent.</h2>
        <p className="lp-body">
          A one-off game for the office, or a mini league that runs all season across as
          many competitions as you like. Free to start, nothing to install.
        </p>
        <div className="lp-cta lp-cta-centred">
          <button className="primary-btn" onClick={() => onNavigate('register')}>
            <UserPlus size={20} />
            Create Account
          </button>
          <button className="secondary-btn" onClick={() => onNavigate('rules')}>
            Read the Rules
          </button>
        </div>
      </section>

    </div>
  );
}

/**
 * A live component frozen for display.
 *
 * `inert` is the honest way to say this: the screens inside are real and would otherwise
 * respond to a tap - `FixtureSelection` in demo mode will happily raise its confirm bar
 * and ask "Confirm Arsenal?" on a page where confirming is not a thing that can happen.
 * `aria-hidden` goes with it because the copy around each screen carries the meaning; a
 * screen reader working through a fixture list here would be reading furniture.
 *
 * The attribute is spelled `inert=""` rather than `inert` because React 18 does not know
 * the property and drops a bare boolean. Pointer events are stopped in CSS as well, for
 * browsers without it.
 */
function DemoScreen({ label, children }) {
  return (
    <div className="lp-screen" inert="" aria-hidden="true" role="presentation" title={label}>
      {children}
    </div>
  );
}

function HowStep({ icon: Icon, title, children }) {
  return (
    <li className="lp-how-step">
      <span className="lp-how-icon"><Icon size={26} /></span>
      <h3>{title}</h3>
      <p>{children}</p>
    </li>
  );
}

export default LandingPage;
